import {
  useCreatePlaybookSetMutation,
  usePlaybookSetMembershipsQuery,
  usePlaybookSetsQuery,
  useUpdatePlaybookSetsMutation,
} from "@qw/graphql";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldCombobox,
  FieldInput,
  FieldTextarea,
  Form,
  Icon,
  Spinner,
  toast,
} from "@qw/ui";
import { type FC, useMemo, useState } from "react";
import { FormProvider, type UseFormReturn, useForm } from "react-hook-form";
import { z } from "zod";

interface PlaybookSetsFieldProps {
  playbookId: string;
  disabled?: boolean;
}

interface PlaybookSetsFormValues {
  playbookSets: string[];
}

const CREATE_NEW_SET_VALUE = "__create_new_set__";

export const PlaybookSetsField: FC<PlaybookSetsFieldProps> = ({ playbookId, disabled }) => {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [isCreatingSet, setIsCreatingSet] = useState(false);

  const { data, loading, refetch } = usePlaybookSetMembershipsQuery({
    variables: { playbookId },
  });

  const { data: setsData, loading: setsLoading, refetch: refetchSets } = usePlaybookSetsQuery();

  const [updateSets, { loading: updating }] = useUpdatePlaybookSetsMutation();

  const defaultValues = useMemo(
    () => ({
      playbookSets: (data?.playbookSetMemberships as string[]) || [],
    }),
    [data?.playbookSetMemberships]
  );

  const form = useForm<PlaybookSetsFormValues>({
    values: defaultValues,
  });

  const handleChange = async (value: string | string[]) => {
    const newSelected = value as string[];

    // Check if user clicked "Create new set"
    if (newSelected.includes(CREATE_NEW_SET_VALUE)) {
      setShowCreateDialog(true);
      return;
    }

    const previousSelected = form.getValues("playbookSets") as string[];

    try {
      await updateSets({
        variables: {
          playbookId,
          setIds: newSelected,
        },
      });
    } catch (error) {
      toast({
        title: "Error updating playbook sets",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive",
      });
      // Revert to previous state on mutation error
      form.setValue("playbookSets", previousSelected as string[]);
      return;
    }

    // Refetch the data to ensure it's in sync
    try {
      await refetch();
    } catch (error) {
      toast({
        title: "Playbook sets updated, but refresh failed",
        description: error instanceof Error ? error.message : "An unexpected error occurred while refreshing data",
        variant: "destructive",
      });
    }
  };

  const handleSetCreated = async (newSetId: string) => {
    setIsCreatingSet(true);
    try {
      // Refetch sets to include the new one
      await refetchSets();

      // Add the new set to the playbook
      // Filter out the CREATE_NEW_SET_VALUE placeholder that may have been added to form state
      const currentSets = (form.getValues("playbookSets") as string[]).filter((id) => id !== CREATE_NEW_SET_VALUE);
      const newSets = [...currentSets, newSetId];

      await updateSets({
        variables: {
          playbookId,
          setIds: newSets,
        },
      });
      await refetch();
    } catch (error) {
      toast({
        title: "Error adding playbook to new set",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsCreatingSet(false);
    }
  };

  const options = useMemo(
    () => [
      ...(setsData?.playbookSets ?? []).map((set) => ({
        label: set.name,
        value: set.id,
      })),
      {
        label: "+ Create a custom set",
        value: CREATE_NEW_SET_VALUE,
        persistent: true,
      },
    ],
    [setsData?.playbookSets]
  );

  if (loading || setsLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Spinner className="size-4" />
        Loading sets...
      </div>
    );
  }

  return (
    <>
      <FormProvider {...form}>
        <FieldCombobox
          label="Playbook Sets"
          name="playbookSets"
          placeholder="Select playbook sets"
          multiple
          options={options}
          onChange={handleChange}
          disabled={disabled || updating || isCreatingSet}
        />
      </FormProvider>

      <CreatePlaybookSetDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onSetCreated={handleSetCreated}
      />
    </>
  );
};

// Schema for creating a new set
const CreatePlaybookSetSchema = z.object({
  name: z.string().min(1, "Name is required").max(128, "Name must be less than 128 characters"),
  description: z.string().max(512, "Description must be less than 512 characters").optional(),
});

type CreatePlaybookSetFormValues = z.infer<typeof CreatePlaybookSetSchema>;

interface CreatePlaybookSetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSetCreated: (newSetId: string) => void;
}

const CreatePlaybookSetFormContent: FC<{
  onOpenChange: (open: boolean) => void;
  isSaving: boolean;
}> = ({ onOpenChange, isSaving }) => {
  return (
    <div className="flex flex-col gap-4">
      <FieldInput label="Name" name="name" placeholder="Enter set name" />
      <FieldTextarea label="Description" name="description" placeholder="Enter a description (optional)" rows={3} />

      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSaving}>
          {isSaving ? (
            <>
              <Icon icon="sync" className="animate-spin mr-2 h-4 w-4" />
              Creating...
            </>
          ) : (
            "Create Set"
          )}
        </Button>
      </DialogFooter>
    </div>
  );
};

const CreatePlaybookSetDialog: FC<CreatePlaybookSetDialogProps> = ({ open, onOpenChange, onSetCreated }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [createPlaybookSet] = useCreatePlaybookSetMutation();

  const defaultValues: CreatePlaybookSetFormValues = {
    name: "",
    description: "",
  };

  const handleSubmit = async (
    values: CreatePlaybookSetFormValues,
    methods: UseFormReturn<CreatePlaybookSetFormValues>
  ) => {
    setIsSaving(true);
    try {
      const result = await createPlaybookSet({
        variables: {
          input: {
            name: values.name,
            description: values.description || undefined,
          },
        },
      });

      const newSetId = result.data?.createPlaybookSet?.id;
      if (newSetId) {
        toast({
          title: "Playbook set created",
          description: `"${values.name}" has been created successfully.`,
        });
        methods.reset(defaultValues);
        onOpenChange(false);
        onSetCreated(newSetId);
      }
    } catch (error) {
      toast({
        title: "Error creating playbook set",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create Playbook Set</DialogTitle>
        </DialogHeader>

        <Form
          mode="onChange"
          onSubmit={handleSubmit}
          defaultValues={defaultValues}
          validationSchema={CreatePlaybookSetSchema}
        >
          <CreatePlaybookSetFormContent onOpenChange={onOpenChange} isSaving={isSaving} />
        </Form>
      </DialogContent>
    </Dialog>
  );
};
